import { Button, Drawer, FlexContainer, FormBuilder } from '@act/storybook';
import { Typography } from '@mui/material';
import { TrackingProvider } from 'react-components/tracking';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import { BulkMemberTransferConfirmationStep } from '../member-index/BulkMemberTransferConfirmationStep';
import { BulkTransferTablePrefill } from '../member-index/resolveBulkTransferTablePrefill.util';
import Loader from '../workflows/Loader';
import { useBulkMemberTransferRequestForm } from './hooks/useBulkMemberTransferRequestForm.hook';

const TRANSFER_DRAWER_WIDTH_PX = 530;

/** Props supplied when opening the table-level bulk transfer drawer. */
export interface BulkMemberTransferRequestUniqueProps {
  tablePrefill?: BulkTransferTablePrefill;
}

/** Complete dialog-context props for the bulk transfer drawer. */
export type BulkMemberTransferRequestDialogProps = GenericDialogProps<boolean> &
  BulkMemberTransferRequestUniqueProps;

/** Dialog-context callback input; the flow closes after a successful submit. */
export type BulkMemberTransferRequestDialogCallbackInput = unknown;

/**
 * Renders the form and confirmation steps for a bulk member transfer.
 *
 * @param props - Drawer state and current-table prefill.
 * @returns Bulk transfer drawer.
 */
export function BulkMemberTransferRequestDialog({
  tablePrefill,
  open,
  setOpen,
}: BulkMemberTransferRequestDialogProps) {
  const {
    form,
    formConfig,
    reasonsStatus,
    drawerStep,
    setDrawerStep,
    confirmationSummary,
    isSubmitting,
    handleClose,
    continueToConfirmation,
    submit,
    retryReasons,
  } = useBulkMemberTransferRequestForm({ open, setOpen, tablePrefill });
  const { control, formState, handleSubmit } = form;

  const footer =
    reasonsStatus === 'success' ? (
      <FlexContainer
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
        width="100%"
        height="auto"
      >
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          variant="outlined"
          trackingName="cancel-button"
        >
          Cancel
        </Button>
        <FlexContainer direction="row" gap={1} height="auto" width="auto">
          {drawerStep === 'confirmation' && (
            <Button
              onClick={() => setDrawerStep('form')}
              disabled={isSubmitting}
              variant="outlined"
              trackingName="back-bulk-transfer-button"
            >
              Back
            </Button>
          )}
          <Button
            disabled={
              isSubmitting ||
              (drawerStep === 'form' && !formState.isValid)
            }
            debounced
            onClick={
              drawerStep === 'form'
                ? handleSubmit(continueToConfirmation)
                : handleSubmit(submit)
            }
            type="button"
            variant="contained"
            trackingName={
              drawerStep === 'form'
                ? 'continue-bulk-transfer-button'
                : 'send-bulk-transfer-request-button'
            }
          >
            {drawerStep === 'form' ? 'Continue' : 'Send request'}
          </Button>
        </FlexContainer>
      </FlexContainer>
    ) : undefined;

  return (
    <TrackingProvider contextName="bulk-member-transfer">
      <Drawer
        open={open}
        onClose={handleClose}
        title={
          drawerStep === 'confirmation'
            ? 'Bulk Transfer Confirmation'
            : 'Request bulk member transfer'
        }
        anchor="primary"
        width={TRANSFER_DRAWER_WIDTH_PX}
        footer={footer}
      >
        <FlexContainer
          direction="column"
          gap={2}
          width="100%"
          height="auto"
          minHeight="0"
        >
          {reasonsStatus === 'failed' && (
            <FlexContainer direction="column" gap={2} height="auto" width="100%">
              <Typography variant="bodyLarge">
                Couldn&apos;t load transfer reasons. Retry to continue.
              </Typography>
              <Button
                onClick={() => void retryReasons()}
                trackingName="retry-bulk-transfer-reasons-button"
              >
                Retry
              </Button>
            </FlexContainer>
          )}
          {(reasonsStatus === 'initial' || reasonsStatus === 'loading') && (
            <FlexContainer direction="column" gap={2} height="auto" width="100%">
              <Loader />
              <Typography variant="bodyLarge">
                Loading transfer options…
              </Typography>
            </FlexContainer>
          )}
          {reasonsStatus === 'success' && drawerStep === 'form' && (
            <FormBuilder
              config={formConfig}
              control={control}
              formState={formState}
              disabled={isSubmitting}
            />
          )}
          {reasonsStatus === 'success' &&
            drawerStep === 'confirmation' &&
            confirmationSummary && (
              <BulkMemberTransferConfirmationStep
                summary={confirmationSummary}
              />
            )}
        </FlexContainer>
      </Drawer>
    </TrackingProvider>
  );
}
